import React, { useState, useEffect, useCallback, useRef } from 'react';
import emailApi from '../services/emailApi';
import { useToast } from '../hooks/useToast';
import ConfirmModal from './ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';

interface Contact {
  id: number;
  name: string;
  email: string;
  company: string;
  phone: string;
  notes: string;
  is_favorite: boolean;
}

const emptyForm = { name: '', email: '', company: '', phone: '', notes: '' };

const ContactsView: React.FC = () => {
  const { toast } = useToast();
  const { confirmState, confirm, cancelConfirm } = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await emailApi.getContacts(search);
      setContacts(data as unknown as Contact[]);
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleSave = async () => {
    if (!form.email) {
      toast.warning('Atenție', 'Email-ul este obligatoriu');
      return;
    }
    try {
      if (editingId !== null) {
        await emailApi.updateContact(editingId, form);
        toast.success('Succes', 'Contact actualizat');
      } else {
        await emailApi.createContact(form);
        toast.success('Succes', 'Contact adăugat');
      }
      setForm(emptyForm);
      setShowForm(false);
      setEditingId(null);
      loadContacts();
    } catch (err) {
      toast.error('Eroare', 'Nu am putut salva contactul');
    }
  };

  const handleEdit = (c: Contact) => {
    setForm({ name: c.name, email: c.email, company: c.company, phone: c.phone, notes: c.notes });
    setEditingId(c.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: 'Confirmare ștergere',
      message: 'Sigur vrei să ștergi acest contact?',
      variant: 'danger',
      confirmText: 'Șterge',
      cancelText: 'Anulează'
    });
    if (!ok) return;
    try {
      await emailApi.deleteContact(id);
      toast.success('Succes', 'Contact șters');
      loadContacts();
    } catch {
      toast.error('Eroare', 'Nu am putut șterge contactul');
    }
  };

  const handleToggleFavorite = async (c: Contact) => {
    try {
      await emailApi.updateContact(c.id, { is_favorite: String(!c.is_favorite) } as unknown as Record<string, string>);
      loadContacts();
    } catch {
      // silently fail
    }
  };

  const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast.warning('Atenție', 'Fișierul CSV este gol sau are doar header');
        return;
      }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const emailIdx = headers.findIndex(h => h.includes('email'));
      const nameIdx = headers.findIndex(h => h.includes('name') || h.includes('nume'));
      const companyIdx = headers.findIndex(h => h.includes('company') || h.includes('companie'));
      const phoneIdx = headers.findIndex(h => h.includes('phone') || h.includes('telefon'));

      if (emailIdx === -1) {
        toast.error('Eroare', 'Nu am găsit coloana "email" în CSV');
        return;
      }

      const parsed: Record<string, string>[] = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim());
        if (!cols[emailIdx]) continue;
        parsed.push({
          email: cols[emailIdx] || '',
          name: nameIdx >= 0 ? cols[nameIdx] || '' : '',
          company: companyIdx >= 0 ? cols[companyIdx] || '' : '',
          phone: phoneIdx >= 0 ? cols[phoneIdx] || '' : ''
        });
      }

      if (parsed.length === 0) {
        toast.warning('Atenție', 'Niciun contact valid găsit în CSV');
        return;
      }

      await emailApi.importContacts(parsed);
      toast.success('Succes', `${parsed.length} contacte importate`);
      loadContacts();
    } catch {
      toast.error('Eroare', 'Nu am putut importa contactele');
    }
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    backgroundColor: 'var(--input-bg, #0f172a)',
    border: '1px solid var(--border-color, #334155)',
    borderRadius: '6px',
    color: 'var(--text-primary, #e2e8f0)',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const btnStyle = (bg: string, color: string = '#fff'): React.CSSProperties => ({
    padding: '6px 14px',
    backgroundColor: bg,
    color,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: 'var(--text-primary, #e2e8f0)' }}>📇 Agenda de Contacte</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={btnStyle('#3b82f6')} onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(!showForm); }}>
            {showForm ? 'Anulează' : '➕ Adaugă Contact'}
          </button>
          <button style={btnStyle('#10b981')} onClick={() => fileInputRef.current?.click()}>
            📥 Import CSV
          </button>
          <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvImport} />
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="🔍 Caută contacte..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={inputStyle}
        />
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div style={{
          backgroundColor: 'var(--card-bg, #1e293b)',
          border: '1px solid var(--border-color, #334155)',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 16px', color: 'var(--text-primary, #e2e8f0)' }}>
            {editingId ? '✏️ Editează Contact' : '➕ Contact Nou'}
          </h3>
          <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>Nume</label>
              <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nume complet" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>Email *</label>
              <input style={inputStyle} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@exemplu.com" type="email" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>Companie</label>
              <input style={inputStyle} value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Companie" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>Telefon</label>
              <input style={inputStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+40..." />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>Note</label>
              <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Note..." />
            </div>
          </div>
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button style={btnStyle('#3b82f6')} onClick={handleSave}>{editingId ? 'Salvează' : 'Adaugă'}</button>
            <button style={btnStyle('transparent', 'var(--text-secondary, #94a3b8)')} onClick={() => { setShowForm(false); setEditingId(null); }}>Anulează</button>
          </div>
        </div>
      )}

      {/* Contact list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary, #94a3b8)' }}>Se încarcă...</div>
      ) : contacts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary, #94a3b8)' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📇</div>
          <p>Nu ai contacte încă. Adaugă unul sau importă din CSV!</p>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'var(--card-bg, #1e293b)',
          border: '1px solid var(--border-color, #334155)',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color, #334155)' }}>
                {['', 'Nume', 'Email', 'Companie', 'Telefon', 'Acțiuni'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', color: 'var(--text-secondary, #94a3b8)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--border-color, #334155)' }}>
                  <td style={{ padding: '10px 16px' }}>
                    <button onClick={() => handleToggleFavorite(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}>
                      {c.is_favorite ? '⭐' : '☆'}
                    </button>
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--text-primary, #e2e8f0)' }}>{c.name || '-'}</td>
                  <td style={{ padding: '10px 16px', color: 'var(--text-primary, #e2e8f0)' }}>{c.email}</td>
                  <td style={{ padding: '10px 16px', color: 'var(--text-secondary, #94a3b8)' }}>{c.company || '-'}</td>
                  <td style={{ padding: '10px 16px', color: 'var(--text-secondary, #94a3b8)' }}>{c.phone || '-'}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <div className="contact-actions" style={{ display: 'flex', gap: '6px' }}>
                      <button style={btnStyle('#3b82f6')} onClick={() => handleEdit(c)}>✏️</button>
                      <button style={btnStyle('#ef4444')} onClick={() => handleDelete(c.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        onConfirm={confirmState.onConfirm}
        onCancel={cancelConfirm}
      />
    </div>
  );
};

export default ContactsView;
